import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import RefreshIcon from '@mui/icons-material/Refresh';

import clsx from 'clsx';

import { ReactComponent as Error403Icon } from '../../../assets/icons/error-pages/error403.svg';
import { ReactComponent as Error404Icon } from '../../../assets/icons/error-pages/error404.svg';
import { ReactComponent as Error500Icon } from '../../../assets/icons/error-pages/error500.svg';
import AppButton from '../../designe/buttons/app-button/AppButton';
import { useStyles } from './InlineContainerError.styles';

interface IInlineContainerErrorProps {
  status: 403 | 404 | 500;
  onRetry?: () => void | Promise<void>;
}

const InlineContainerError = (props: IInlineContainerErrorProps) => {
  const { status, onRetry } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);

  const config = useMemo(() => {
    if (status === 500) {
      return {
        icon: Error500Icon,
        title: t('inlineError.server.title'),
        description: t('inlineError.server.description'),
        isServer: true,
      };
    }
    if (status === 404) {
      return {
        icon: Error404Icon,
        title: t('inlineError.notFound.title'),
        description: t('inlineError.notFound.description'),
        isServer: false,
      };
    }
    return {
      icon: Error403Icon,
      title: t('inlineError.forbidden.title'),
      description: t('inlineError.forbidden.description'),
      isServer: false,
    };
  }, [status, t]);

  const Icon = config.icon;

  return (
    <Box
      className={clsx(classes.root, !config.isServer && classes.rootNeutral)}>
      <Box
        className={clsx(
          classes.iconCircle,
          !config.isServer && classes.iconCircleNeutral,
        )}>
        <Icon width={30} height={30} />
      </Box>
      <Typography
        variant="h2"
        className={clsx(
          classes.statusCode,
          !config.isServer && classes.statusCodeNeutral,
        )}>
        {status}
      </Typography>
      <Typography variant="h2">{config.title}</Typography>
      <Typography variant="text-md" className={classes.description}>
        {config.description}
      </Typography>
      {config.isServer && (
        <AppButton variant="contained" onClick={onRetry} icon={<RefreshIcon />}>
          {t('inlineError.server.reload')}
        </AppButton>
      )}
    </Box>
  );
};

export default InlineContainerError;
